<template>
  <div class="flex mt-8 bg-surface items-center rounded-lg py-4">
    <div
      v-if="!selectedCategory || subcategoryButtons.length === 0"
      class="hidden md:flex flex-wrap gap-2 px-4 border-r flex-1"
    >
      <Button
        v-for="categoryButton in categoryButtons"
        :key="categoryButton.label"
        :label="categoryButton.label"
        :color="ButtonColor.TERTIARY"
        :variant="
          selectedCategory === categoryButton.category
            ? ButtonVariant.FILLED
            : ButtonVariant.OUTLINED
        "
        :aria-selected="
          (selectedCategory === categoryButton.category).toString()
        "
        @click="setCategoryFilter(categoryButton.category)"
      />
    </div>
    <div v-else class="hidden md:flex flex-wrap gap-2 px-4 border-r flex-1">
      <div class="w-full flex items-center">
        <IconButton
          :icon-name="IconName.ARROW_LEFT_CIRCLE"
          label="Back to categories filter"
          :size="ButtonSize.SM"
          :variant="ButtonVariant.TEXT"
          :color="ButtonColor.SECONDARY"
          @click="setCategoryFilter(null)"
        />
        <span class="ml-2 text-on-surface-variant">
          {{ selectedCategory.name }}
        </span>
      </div>

      <Button
        v-for="subcategoryButton in subcategoryButtons"
        :key="subcategoryButton.label"
        :label="subcategoryButton.label"
        class="whitespace-nowrap"
        :size="ButtonSize.SM"
        :color="ButtonColor.TERTIARY"
        :variant="
          subcategoryButton.value === filters.subcategoryId
            ? ButtonVariant.FILLED
            : ButtonVariant.OUTLINED
        "
        :aria-selected="
          (subcategoryButton.value === filters.subcategoryId).toString()
        "
        @click="filters.subcategoryId = subcategoryButton.value"
      />
    </div>

    <div class="flex md:flex-row-reverse gap-2 px-3 w-full md:w-1/3">
      <SearchInput
        v-model="filters.search"
        class="flex-1"
        :debounce="DEBOUNCE_DELAY_MS"
        hide-details
      />
      <FiltersModal v-model="filters" :hub="props.hub" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { HubDto } from '~/api/hubs';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconName,
} from '@exfluential/ui';
import { SearchInput } from '~/components/inputs/search-input';
import { FiltersModal } from '../filters-modal';
import { HubFilters } from '../../_types';

type Props = {
  hub: HubDto;
};

const props = defineProps<Props>();
const filters = defineModel<HubFilters>({ required: true });

const { t } = useI18n();

const selectedCategory = computed<null | HubDto['categories'][number]>(() => {
  if (filters.value.categoryId === null) {
    return null;
  }

  const category = props.hub.categories.find(
    (category) => category.id === filters.value.categoryId,
  );

  return category ?? null;
});

const categoryButtons = computed(() => {
  if (!props.hub) {
    return [];
  }
  return [
    { category: null, label: t('filters-modal.all') },
    ...props.hub.categories.map((category) => ({
      category,
      label: category.name,
    })),
  ];
});
const subcategoryButtons = computed(() => {
  if (
    selectedCategory.value === null ||
    selectedCategory.value.subcategories.length === 0
  ) {
    return [];
  }
  return [
    { value: null, label: 'All' },
    ...selectedCategory.value.subcategories.map((subcategory) => ({
      value: subcategory.id,
      label: subcategory.name,
    })),
  ];
});
const setCategoryFilter = (category: null | HubDto['categories'][number]) => {
  filters.value.categoryId = category?.id ?? null;
  filters.value.subcategoryId = null;
};
</script>
